import React from "react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { getPlatformDisplayName } from "../../utils/iconUtils";

interface PlatformDistributionProps {
  title: string;
  subtitle: string;
  data: Record<string, number>;
  isLoading?: boolean;
}

const PlatformDistributionChart: React.FC<PlatformDistributionProps> = ({
  title,
  subtitle,
  data,
  isLoading = false,
}) => {
  const theme = useTheme();

  // Transform the data for the pie chart and use proper display names
  const chartData = Object.entries(data).map(([key, value]) => ({
    name: getPlatformDisplayName(key),
    value,
  }));

  const filteredChartData = chartData.filter((item) => item.value > 0);

  // Colors for the pie chart
  const COLORS = [
    theme.palette.secondary.main,
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.success.main,
  ];

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number }>;
  }) => {
    if (active && payload && payload.length) {
      // Calculate the percentage correctly using the same formula as the label
      const percentage =
        total > 0 ? Math.round((payload[0].value / total) * 100) : 0;

      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: "10px",
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "4px",
          }}
        >
          <Typography variant="body2" color="textPrimary">
            {`${payload[0].name}: ${percentage}%`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Calculate total for percentage
  const total = filteredChartData.reduce((sum, item) => sum + item.value, 0);

  if (!filteredChartData.length || total === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: "100%",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.grey[200]}`,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {subtitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {"No platform distribution data available"}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {subtitle}
      </Typography>

      <Box sx={{ width: "100%", height: 250 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={filteredChartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) =>
                  `${name}: ${Math.round((value / total) * 100)}%`
                }
              >
                {filteredChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default PlatformDistributionChart;
